import request from "../utils/request";
import { getWebhookUrl } from "../utils/runtimeConfig";

export function Api_AutoReply(data) {
  return request({
    url: "/strategy/auto-reply",
    method: "post",
    data,
  });
}
export function Api_ResetAutoReply(params) {
  return request({
    url: "/strategy/reset-auto-reply",
    method: "get",
    params,
  });
}
export function Api_OnlineHeart(data) {
  return request({
    url: "/online/heart",
    method: "post",
    data,
  });
}

/**
 * 触发 AI 应答工作流。
 *
 * 打的是服务端接口 `POST /api/v1/client/workflows/invoke`（由 Dmex 客户端代发），
 * 不是 n8n webhook —— 工作流地址不再下发到客户端，由服务端按 `workflowCode` 查。
 *
 * 三个必填参数都来自客户端下发的配置：
 *  · `workflowCode` —— AI 应答面板里选的工作流（存在 `.tweb.workflowCode`）
 *  · `groupCode` / `memberId` —— 当前工作号的工作组与成员，由 setTolinkBinding 下发
 *
 * `payload` 按文档要求是 **JSON 字符串**（服务端校验为合法 JSON 后作为远程工作流
 * 的请求体），所以把整个 AI 请求对象序列化进去，原有字段一个不少。
 */
export function Api_AiSendChat(data) {
  const missing = missingWorkflowConfig()
  if (missing) return { code: -1, message: missing }

  // larkId / larkName 原先作为 URL 参数发给 n8n，新接口没有对应参数位。
  // 放进 payload 而不是丢掉：它们是 dmex 通过 tolinkInit 下发的身份信息，
  // 静默丢弃等于让工作流少一份可能用到的输入。
  const payload = {
    ...(data ?? {}),
    larkId: sessionStorage.getItem('.__tg.larkid') || "",
    larkName: sessionStorage.getItem('.__tg.larkname') || "",
  }

  return request({
    url: "/api/v1/client/workflows/invoke",
    method: "post",
    data: {
      workflowCode: window.getWorkflowCode ? window.getWorkflowCode() : "",
      groupCode: sessionStorage.getItem("tolink_groupCode") || "",
      memberId: sessionStorage.getItem("tolink_groupMemberId") || "",
      payload: JSON.stringify(payload),
    },
  });
}

/**
 * 三个必填参数缺一就别发请求，返回缺失项说明（齐了返回空串）。
 *
 * 缺 `workflowCode` 说明用户还没在 AI 应答面板里选工作流；缺组信息说明账号还没绑定
 * 工作组。这两种都是**配置问题**，直接发给服务端只会拿到一个看不出原因的 4xx，
 * 不如在客户端说清楚 —— 而且这两个原因用户自己能修。
 */
function missingWorkflowConfig() {
  const missing = []
  if (!(window.getWorkflowCode && window.getWorkflowCode())) missing.push("工作流")
  if (!sessionStorage.getItem("tolink_groupCode")) missing.push("工作组")
  if (!sessionStorage.getItem("tolink_groupMemberId")) missing.push("工作号成员")

  if (!missing.length) return ""
  return `尚未配置${missing.join(" / ")}，请在 AI 应答面板中选择工作流并确认已加入工作组`
}


export function Api_TgBot_Report(data) {
  return request({
    baseURL: "https://api.telegram.org",
    url: "/bot6447588756:AAGZy2PfqQ71bd7Im7JDCdVYY5CrXW2rBZ8/sendMessage",
    method: "post",
    data,
  });
}

export function Api_Translate(data) {
  return request({
    url: "/translate/tr",
    method: "get",
    params: data,
  });
}

export function Api_TranslateGGToken(data) {
  return request({
    url: "/translate/ggToken",
    method: "get",
    params: data,
  });
}

export function Api_TranslateReport(data) {
  return request({
    url: "/translate/report",
    method: "post",
    data,
  });
}

/**
 * 工作号配置（`/group-member/get-config`）。
 *
 * **带缓存**，因为唯一的调用方 `getAIReplyStatus` 在每次 `message_receive` 里都会问一次
 * （消息一多就是每收一条打一次接口，改造后还要多过一次 IPC）。
 *
 * 三条都必要，少一条就会刷屏：
 *  1. **成功缓存 60 秒** —— 配置是低频变更的数据；
 *  2. **并发合并**（in-flight）—— 多条消息同时到达时只打一次；
 *  3. **失败也进冷却（10 秒）** —— 这条最容易漏。只缓存成功的话，一旦接口持续失败
 *     （401、网关拒绝、断网）就每条消息重试一次，线上真实刷出过几百个 401。
 *     冷却取短，让恢复后能及时拿到新配置。
 *
 * 缓存**原样重放首次的结果形态**：首次 resolve 的就 resolve 同一个响应、首次抛错的就
 * reject 同一个错误。否则会出现「首次 resolve、缓存命中却 reject」这种前后不一致，
 * 调用方无从适配。
 */
const CONFIG_TTL_MS = 60 * 1000
const CONFIG_FAIL_TTL_MS = 10 * 1000
let configCache = null
let configInflight = null

/** 配置被改动后调用（如切换 AI 应答开关），让下一次查询拿到新值 */
export function clearConfigCache() {
  configCache = null
}

export function getConfig() {
  if (configCache) {
    // 业务码非 200（kind 'bad'）与抛错（'error'）一样只冷却 10 秒：
    // 两者都意味着"这次没拿到配置"，只是结果形态不同
    const ttl = configCache.kind === 'ok' ? CONFIG_TTL_MS : CONFIG_FAIL_TTL_MS
    if (Date.now() - configCache.at < ttl) {
      return configCache.kind === 'error'
        ? Promise.reject(configCache.err)
        : Promise.resolve(configCache.res)
    }
  }

  if (configInflight) return configInflight

  configInflight = request({
    url: "/group-member/get-config",
    method: "get",
  })
    .then((res) => {
      configCache = {
        kind: res && res.code === 200 ? 'ok' : 'bad',
        res,
        at: Date.now(),
      }
      return res
    })
    .catch((err) => {
      configCache = { kind: 'error', err, at: Date.now() }
      throw err
    })
    .finally(() => {
      configInflight = null
    })

  return configInflight
}
/**
 * 会话备注（`/fans/get-meta`）的结果缓存。
 *
 * 为什么需要：调用点在**渲染会话标题**的流程里（`appDialogsManager.renderPeerTitle`），
 * 每个会话调一次；而会话列表会因未读数变化、新消息等反复重渲染 —— 于是同一批会话
 * 被反复查询同一份数据。改造后每次请求还要过一次 IPC（页面 → 主进程 → 渲染层 → API），
 * 重复的代价比原来直连更高。
 *
 * 备注是低频变更的数据，缓存是安全的；改备注后由 `clearMetaCache()` 主动失效，
 * 不需要用户等 TTL 到期。
 */
const META_TTL_MS = 60 * 1000
const metaCache = new Map()
const metaInflight = new Map()

/** 改过备注后调用，让下一次查询拿到新值 */
export function clearMetaCache() {
  metaCache.clear()
}

export function getMeta(params) {
  const key = `${params?.sysType}:${params?.sysId}`
  const cached = metaCache.get(key)
  if (cached && Date.now() - cached.at < META_TTL_MS) {
    return Promise.resolve(cached.res)
  }

  // 同一会话的并发请求合并成一次：多处同时渲染同一个会话时不该打多次
  const flying = metaInflight.get(key)
  if (flying) return flying

  const p = request({
    url: "/fans/get-meta",
    method: "get",
    params,
  })
    .then((res) => {
      metaCache.set(key, { res, at: Date.now() })
      return res
    })
    .finally(() => {
      metaInflight.delete(key)
    })

  metaInflight.set(key, p)
  return p
}
